/**
 * RevisionHub Page
 *
 * Entry point to the Revision Studio feature.
 * Shows available revision samples and user's revision history.
 */

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ChevronLeft, RefreshCw, Filter, FileEdit, Loader2, History } from 'lucide-react'
import { Button, Card, CardContent } from '@/components/ui'
import { SampleCard, RevisionCard } from '@/components/revise'
import { useFamily } from '@/context/FamilyContext'
import { useToast } from '@/context/ToastContext'
import {
  getRevisionSamples,
  getRevisionsForChild,
  getRevisionStats,
  type SampleFilters,
} from '@/services/revisionService'
import type { RevisionSample, StoryRevision, ChallengeDifficulty } from '@/types/database'

const DIFFICULTY_OPTIONS: Array<{ value: ChallengeDifficulty | 'all'; label: string }> = [
  { value: 'all', label: 'All Levels' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
]

export function RevisionHub() {
  const { activeWriter } = useFamily()
  const { showToast } = useToast()

  const [samples, setSamples] = useState<RevisionSample[]>([])
  const [revisions, setRevisions] = useState<StoryRevision[]>([])
  const [stats, setStats] = useState({ total: 0, completed: 0, inProgress: 0, averageImprovement: 0 })
  const [isLoading, setIsLoading] = useState(true)
  const [selectedDifficulty, setSelectedDifficulty] = useState<ChallengeDifficulty | 'all'>('all')

  // Load samples and revisions
  useEffect(() => {
    async function loadData() {
      if (!activeWriter) return

      setIsLoading(true)
      try {
        const filters: SampleFilters = {}
        if (selectedDifficulty !== 'all') {
          filters.difficulty = selectedDifficulty
        }

        const [sampleData, revisionData, statsData] = await Promise.all([
          getRevisionSamples(filters),
          getRevisionsForChild(activeWriter.id),
          getRevisionStats(activeWriter.id),
        ])

        setSamples(sampleData)
        setRevisions(revisionData)
        setStats(statsData)
      } catch (error) {
        console.error('Error loading revision data:', error)
        showToast('Failed to load revision samples', 'error')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [activeWriter, selectedDifficulty, showToast])

  // Check if there's an in-progress revision for a sample
  const hasInProgressRevision = (sampleId: string): boolean => {
    return revisions.some(
      r => r.sample_id === sampleId && r.status === 'in_progress'
    )
  }

  // Get title for a revision based on source
  const getRevisionTitle = (revision: StoryRevision): string => {
    if (revision.source_type === 'sample') {
      const sample = samples.find(s => s.id === revision.sample_id)
      return sample?.title || 'Sample Revision'
    }
    return 'Story Revision'
  }

  // Separate in-progress and completed revisions
  const inProgressRevisions = revisions.filter(r => r.status === 'in_progress')
  const completedRevisions = revisions.filter(r => r.status === 'completed')

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)]">
      {/* Header */}
      <header className="bg-[var(--color-kids-surface)] border-b border-[var(--color-kids-surface-alt)] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <RefreshCw className="w-6 h-6 text-[var(--color-kids-accent)]" />
                <h1 className="font-display font-bold text-xl text-[var(--color-kids-text)]">
                  Revision Studio
                </h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Intro */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <h2 className="font-display font-bold text-lg text-[var(--color-kids-text)] mb-2">
              Make Your Stories Better
            </h2>
            <p className="text-[var(--color-kids-text-muted)]">
              Great writers are great revisers! Practice fixing "broken" stories or revise your own challenge submissions.
              See how much you can improve your structure score.
            </p>
          </CardContent>
        </Card>

        {/* In-Progress Revisions */}
        {inProgressRevisions.length > 0 && (
          <section className="mb-8">
            <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)] mb-4 flex items-center gap-2">
              <FileEdit className="w-5 h-5 text-[var(--color-kids-primary)]" />
              Continue Revising
            </h3>
            <div className="space-y-3">
              {inProgressRevisions.map((revision, index) => (
                <motion.div
                  key={revision.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <RevisionCard
                    revision={revision}
                    title={getRevisionTitle(revision)}
                  />
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Sample Filters */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          <Filter className="w-4 h-4 text-[var(--color-kids-text-muted)] flex-shrink-0" />
          {DIFFICULTY_OPTIONS.map(option => (
            <button
              key={option.value}
              onClick={() => setSelectedDifficulty(option.value)}
              className={`
                px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors
                ${
                  selectedDifficulty === option.value
                    ? 'bg-[var(--color-kids-accent)] text-white'
                    : 'bg-[var(--color-kids-surface)] text-[var(--color-kids-text-muted)] hover:bg-[var(--color-kids-surface-alt)]'
                }
              `}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--color-kids-accent)]" />
          </div>
        )}

        {/* Samples Grid */}
        {!isLoading && (
          <div className="space-y-8">
            {/* Available Samples */}
            <section>
              <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)] mb-4">
                Fix a Broken Story
              </h3>
              {samples.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2">
                  {samples.map((sample, index) => (
                    <motion.div
                      key={sample.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <SampleCard
                        sample={sample}
                        hasInProgress={hasInProgressRevision(sample.id)}
                      />
                    </motion.div>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center">
                    <RefreshCw className="w-12 h-12 mx-auto mb-3 text-[var(--color-kids-text-light)]" />
                    <p className="text-[var(--color-kids-text-muted)]">
                      No samples found for this level. Try a different filter!
                    </p>
                  </CardContent>
                </Card>
              )}
            </section>

            {/* Revise Your Own Submissions CTA */}
            <section>
              <Card className="bg-[var(--color-kids-primary)]/5 border-[var(--color-kids-primary)]/20">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[var(--color-kids-primary)]/10 flex items-center justify-center">
                      <FileEdit className="w-6 h-6 text-[var(--color-kids-primary)]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)]">
                        Revise Your Challenge Stories
                      </h3>
                      <p className="text-sm text-[var(--color-kids-text-muted)]">
                        After completing a challenge, you can revise your story to improve your score.
                      </p>
                    </div>
                    <Link to="/challenge">
                      <Button variant="secondary" size="sm">
                        Go to Challenges
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Completed Revisions */}
            {completedRevisions.length > 0 && (
              <section>
                <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)] mb-4 flex items-center gap-2">
                  <History className="w-5 h-5 text-[var(--color-kids-text-muted)]" />
                  Completed Revisions
                </h3>
                <div className="space-y-3">
                  {completedRevisions.slice(0, 5).map((revision, index) => (
                    <motion.div
                      key={revision.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <RevisionCard
                        revision={revision}
                        title={getRevisionTitle(revision)}
                      />
                    </motion.div>
                  ))}
                </div>
              </section>
            )}

            {/* Stats Section */}
            {stats.total > 0 && (
              <section>
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)] mb-4">
                      Your Revision Progress
                    </h3>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-display font-bold text-[var(--color-kids-accent)]">
                          {stats.completed}
                        </p>
                        <p className="text-sm text-[var(--color-kids-text-muted)]">
                          Completed
                        </p>
                      </div>
                      <div>
                        <p className="text-2xl font-display font-bold text-[var(--color-kids-primary)]">
                          {stats.inProgress}
                        </p>
                        <p className="text-sm text-[var(--color-kids-text-muted)]">
                          In Progress
                        </p>
                      </div>
                      <div>
                        <p className="text-2xl font-display font-bold text-[var(--color-kids-success)]">
                          +{stats.averageImprovement}
                        </p>
                        <p className="text-sm text-[var(--color-kids-text-muted)]">
                          Avg. Improvement
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  )
}

export default RevisionHub
